import { AppWindow, Globe, Plus, Search, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  useAppCatalog,
  useDeleteCatalogEntry,
  useSaveCatalogEntry,
  useSetCategoryOverride,
  useTeams,
  type AppCatalogEntry,
  type AppCategory,
} from "@/api/queries";
import { useCurrentUser } from "@/app/auth";
import { CategoryLabel } from "@/components/common/Category";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Field } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { ApiError } from "@/api/client";
import { categories } from "@/lib/categories";
import { errorText } from "@/lib/errors";

type Kind = "all" | "app" | "url";

export function CategoriesPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const admin = me.role === "admin";
  const catalog = useAppCatalog();
  const [kind, setKind] = useState<Kind>("all");
  const [category, setCategory] = useState<AppCategory | "">("");
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [teamsOf, setTeamsOf] = useState<AppCatalogEntry | null>(null);
  const [deleting, setDeleting] = useState<AppCatalogEntry | null>(null);
  const del = useDeleteCatalogEntry();

  const q = query.trim().toLowerCase();
  const items = (catalog.data ?? []).filter(
    (e) =>
      (kind === "all" || e.kind === kind) &&
      (!category || e.category === category) &&
      (!q || e.display_name.toLowerCase().includes(q) || e.match_value.toLowerCase().includes(q)),
  );
  const kindOptions = (["all", "app", "url"] as const).map((k) => ({
    value: k,
    label: t(`apps.kinds.${k}`),
  }));

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3 pb-4">
        <div>
          <CardTitle>{t("settings.categories.title")}</CardTitle>
          <CardDescription className="max-w-2xl">{t("settings.categories.description")}</CardDescription>
        </div>
        {admin && (
          <Button onClick={() => setAdding(true)}>
            <Plus /> {t("settings.categories.add")}
          </Button>
        )}
      </CardHeader>
      <div className="flex flex-col gap-3 px-5 pb-4">
        {!admin && <Alert>{t("settings.categories.readOnly")}</Alert>}
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            aria-label={t("settings.categories.kind")}
            value={kind}
            onChange={setKind}
            options={kindOptions}
          />
          <NativeSelect
            aria-label={t("categories.title")}
            value={category}
            onChange={(e) => setCategory(e.target.value as AppCategory | "")}
          >
            <option value="">{t("common.all")}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {t(`categories.${c}`)}
              </option>
            ))}
          </NativeSelect>
          <div className="relative ml-auto w-full sm:w-64">
            <Search
              className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label={t("common.search")}
              placeholder={t("common.search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 pl-9"
            />
          </div>
        </div>
      </div>
      {catalog.isError ? (
        <div className="px-5 pb-5">
          <Alert variant="danger">{errorText(t, catalog.error)}</Alert>
        </div>
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>{t("settings.categories.columns.name")}</TH>
              <TH>{t("settings.categories.columns.match")}</TH>
              <TH>{t("settings.categories.columns.category")}</TH>
              <TH>{t("settings.categories.columns.teams")}</TH>
              {admin && <TH className="w-12" />}
            </tr>
          </THead>
          <tbody>
            {catalog.isPending ? (
              [0, 1, 2].map((i) => (
                <TR key={i}>
                  <TD colSpan={5}>
                    <Skeleton className="h-6 w-full" />
                  </TD>
                </TR>
              ))
            ) : items.length === 0 ? (
              <TR>
                <TD colSpan={5} className="py-8 text-center text-muted">
                  {t("settings.categories.empty")}
                </TD>
              </TR>
            ) : (
              items.map((e) => (
                <EntryRow
                  key={e.id}
                  entry={e}
                  admin={admin}
                  onTeams={() => setTeamsOf(e)}
                  onDelete={() => setDeleting(e)}
                />
              ))
            )}
          </tbody>
        </Table>
      )}
      {adding && <AddDialog onClose={() => setAdding(false)} />}
      {teamsOf && (
        <TeamsDialog
          entry={catalog.data?.find((e) => e.id === teamsOf.id) ?? teamsOf}
          onClose={() => setTeamsOf(null)}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t("settings.categories.deleteTitle", { name: deleting.display_name })}
          description={t("settings.categories.deleteDescription")}
          confirmLabel={t("common.delete")}
          danger
          errorText={(err) => errorText(t, err)}
          onConfirm={() => del.mutateAsync(deleting.id)}
          onClose={() => setDeleting(null)}
        />
      )}
    </Card>
  );
}

type EntryRowProps = {
  entry: AppCatalogEntry;
  admin: boolean;
  onTeams: () => void;
  onDelete: () => void;
};

function EntryRow({ entry: e, admin, onTeams, onDelete }: EntryRowProps) {
  const { t } = useTranslation();
  const save = useSaveCatalogEntry();
  const Icon = e.kind === "url" ? Globe : AppWindow;
  const teams = e.overrides.length;
  return (
    <TR>
      <TD>
        <span className="flex items-center gap-2 font-medium">
          <Icon className="size-4 shrink-0 text-muted" aria-hidden="true" />
          {e.display_name}
        </span>
      </TD>
      <TD className="font-mono text-xs text-muted">{e.match_value}</TD>
      <TD>
        {admin ? (
          <NativeSelect
            aria-label={t("apps.categoryFor", { name: e.display_name })}
            value={e.category}
            disabled={save.isPending}
            onChange={(ev) =>
              save.mutate({
                entryId: e.id,
                display_name: e.display_name,
                category: ev.target.value as AppCategory,
              })
            }
            className="h-8 text-xs"
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {t(`categories.${c}`)}
              </option>
            ))}
          </NativeSelect>
        ) : (
          <CategoryLabel category={e.category} />
        )}
        {save.isError && <div className="mt-1 text-xs text-danger">{errorText(t, save.error)}</div>}
      </TD>
      <TD>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {e.overrides.map((o) => (
            <span key={o.team_id} className="flex items-center gap-1 text-xs">
              <span className="text-muted">{o.team_name}:</span>
              <CategoryLabel category={o.category} />
            </span>
          ))}
          {teams === 0 && !admin && <span className="text-muted">—</span>}
          {admin && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onTeams}>
              {t("settings.categories.configure")}
            </Button>
          )}
        </div>
      </TD>
      {admin && (
        <TD className="text-right">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted hover:text-danger"
            aria-label={t("settings.categories.deleteTitle", { name: e.display_name })}
            onClick={onDelete}
          >
            <Trash2 />
          </Button>
        </TD>
      )}
    </TR>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const save = useSaveCatalogEntry();
  const [kind, setKind] = useState<"app" | "url">("url");
  const [match, setMatch] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState<AppCategory>("productive");
  const fieldError = save.error instanceof ApiError ? save.error.fields.match_value : undefined;

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    save.mutate(
      { kind, match_value: match.trim(), display_name: name.trim() || match.trim(), category },
      { onSuccess: onClose },
    );
  };
  const kindOptions = (["url", "app"] as const).map((k) => ({ value: k, label: t(`apps.kinds.${k}`) }));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t("settings.categories.addTitle")} description={t("settings.categories.recalc")}>
        <form onSubmit={submit} className="flex flex-col gap-4 p-5">
          <Segmented
            aria-label={t("settings.categories.kind")}
            value={kind}
            onChange={setKind}
            options={kindOptions}
          />
          <Field
            label={kind === "url" ? t("settings.categories.matchUrl") : t("settings.categories.matchApp")}
            hint={
              kind === "url" ? t("settings.categories.matchUrlHint") : t("settings.categories.matchAppHint")
            }
            error={fieldError && errorText(t, save.error)}
          >
            <Input required value={match} onChange={(e) => setMatch(e.target.value)} autoFocus />
          </Field>
          <Field label={t("settings.categories.displayName")}>
            <Input value={name} placeholder={match} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t("categories.title")}>
            <NativeSelect
              value={category}
              onChange={(e) => setCategory(e.target.value as AppCategory)}
              className="h-10 w-full"
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {t(`categories.${c}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {save.isError && !fieldError && <Alert variant="danger">{errorText(t, save.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={save.isPending || !match.trim()}>
              {t("common.create")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeamsDialog({ entry, onClose }: { entry: AppCatalogEntry; onClose: () => void }) {
  const { t } = useTranslation();
  const teams = useTeams();
  const setOverride = useSetCategoryOverride();
  const current = new Map(entry.overrides.map((o) => [o.team_id, o.category]));
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={t("settings.categories.teamsTitle")}
        description={t("settings.categories.teamsDescription", { name: entry.display_name })}
      >
        <div className="flex flex-col gap-3 p-5">
          {teams.isPending ? (
            <Skeleton className="h-10 w-full" />
          ) : (teams.data ?? []).length === 0 ? (
            <p className="text-muted">{t("settings.categories.noTeams")}</p>
          ) : (
            <ul className="flex flex-col divide-y rounded-lg border">
              {(teams.data ?? []).map((team) => (
                <li key={team.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="font-medium">{team.name}</span>
                  <NativeSelect
                    aria-label={t("apps.categoryFor", { name: team.name })}
                    value={current.get(team.id) ?? ""}
                    disabled={setOverride.isPending}
                    onChange={(e) =>
                      setOverride.mutate({
                        entryId: entry.id,
                        teamId: team.id,
                        category: (e.target.value || null) as AppCategory | null,
                      })
                    }
                    className="h-8 text-xs"
                  >
                    <option value="">
                      {t("settings.categories.inherit")} ({t(`categories.${entry.category}`)})
                    </option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {t(`categories.${c}`)}
                      </option>
                    ))}
                  </NativeSelect>
                </li>
              ))}
            </ul>
          )}
          {setOverride.isError && <Alert variant="danger">{errorText(t, setOverride.error)}</Alert>}
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="secondary">{t("common.close")}</Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
